import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowTopRightOnSquareIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import DOMPurify from "dompurify";
import { getProductById, getStores } from "../../../api/productApi";

/* ✅ Persistent cache (file level) */
const detailsCache = new Map();

export default function BookInformation({ book, onClose }) {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [details, setDetails] = useState(null);
    const [stores, setStores] = useState([]);

    // Get current store info
    const selectedStoreRaw = localStorage.getItem("selectedStore");
    const currentStore = selectedStoreRaw ? JSON.parse(selectedStoreRaw) : null;
    const currentStoreName = currentStore?.name || "-";

    useEffect(() => {
        if (!book?.id) return;

        let ignore = false;

        // Show cached instantly
        if (detailsCache.has(book.id)) {
            setDetails(detailsCache.get(book.id));
            setLoading(false);
        } else {
            setLoading(true);
        }

        async function fetchDetails() {
            try {
                setError("");

                // Fetch product details and stores list in parallel
                const [res, storesRes] = await Promise.all([
                    getProductById({ id: book.id, token }),
                    getStores({ token })
                ]);

                if (ignore) return;

                detailsCache.set(book.id, res);
                setDetails(res);
                setStores(Array.isArray(storesRes?.data) ? storesRes.data : []);
            } catch (err) {
                if (!ignore) setError("Failed to load book details");
            } finally {
                if (!ignore) setLoading(false);
            }
        }

        fetchDetails();

        return () => {
            ignore = true;
        };
    }, [book?.id, token]);

    if (!details && loading) {
        return (
            <div className="flex items-center justify-center h-[300px] text-gray-400">
                Loading book details…
            </div>
        );
    }

    if (error && !details) {
        return (
            <div className="text-center space-y-4">
                <p className="text-red-500">{error}</p>
                <button
                    onClick={onClose}
                    className="bg-black text-white px-4 py-2 rounded"
                >
                    Close
                </button>
            </div>
        );
    }

    if (!details) return null;

    /* ---------- DATA NORMALIZATION ---------- */
    const image = details.image || "https://via.placeholder.com/260x400?text=No+Image";
    const name = details.name || "-";
    const authors = Array.isArray(details.author) && details.author.length
        ? details.author.map((a) => a.name).join(", ")
        : "-";
    const genres = Array.isArray(details.genre) && details.genre.length
        ? details.genre.map((g) => g.name)
        : [];
    const illustratedBy = details.illustrated_by || "-";
    const credits = details.price ? `${details.price} Credit${Number(details.price) !== 1 ? 's' : ''}` : "-";
    const stockStatus = details.stock_status || "-";
    const description = details.description || "No description available";

    // Build the list of copies for the current store.
    const rawCopies = Array.isArray(details.copys) ? details.copys : [];

    const currentStoreCopies = rawCopies.filter((c) => {
        if (!currentStore?.id) return false;
        return String(c?.store?.id) === String(currentStore.id);
    });

    const copiesToDisplay = currentStoreCopies.map((c) => {
        return {
            copyId: c?.copy_id || "-",
            status: c?.status || details.stock_status || "-",
            memberName: c?.member_name || c?.member || "",
            memberId: c?.member_id || c?.user_id || null,
            shelfId: c?.shelf_id || c?.shelf || "-"
        };
    });

    const totalCopies = rawCopies.length;

    // Filter other stores (excluding current store) and compute available copies for each
    const otherStores = stores
        .filter((s) => {
            if (!currentStore?.id) return true;
            return String(s.id) !== String(currentStore.id);
        })
        .map((s) => {
            const availableCopiesCount = rawCopies.filter((c) => {
                const isSameStore = String(c?.store?.id) === String(s.id);
                const isAvailable = String(c?.status).toLowerCase() === "available";
                return isSameStore && isAvailable;
            }).length;

            return {
                ...s,
                available_copies: availableCopiesCount,
            };
        });

    // WooCommerce product URL and edit URL from API
    const viewProductUrl = details.product_view_link || "";
    const editProductUrl = details.product_edit_link || "";

    const getStatusBadge = (status) => {
        const s = status?.toLowerCase() || "";
        if (s.includes("circulation")) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    {status}
                </span>
            );
        } else if (s.includes("available")) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                    {status}
                </span>
            );
        } else {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                    {status}
                </span>
            );
        }
    };

    return (
        <div className="max-w-6xl mx-auto w-full">
            {/* HEADER */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Book Information</h2>
                {loading && <span className="text-xs text-gray-400 animate-pulse">Refreshing…</span>}
            </div>

            {/* UPPER PANEL: IMAGE & SPECIFICATION */}
            <div className="flex flex-col md:flex-row gap-8 pb-6">
                {/* LEFT: IMAGE & ACTIONS */}
                <div className="flex-shrink-0 flex flex-col items-start gap-4">
                    <img
                        src={image}
                        alt={name}
                        className="w-[280px] h-[370px] object-cover rounded-lg shadow-md border"
                    />

                    <div className="flex flex-col gap-2 mt-2 w-full text-sm font-semibold">
                        <a
                            href={viewProductUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5"
                        >
                            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                            View product
                        </a>
                        <a
                            href={editProductUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5"
                        >
                            <PencilSquareIcon className="w-4 h-4" />
                            Edit product details
                        </a>
                    </div>
                </div>

                {/* RIGHT: SPECIFICATION LIST */}
                <div className="flex-1 flex flex-col justify-between">
                    <div className="space-y-4">
                        <h3 className="text-3xl font-extrabold text-gray-900 leading-tight">{name}</h3>

                        <div className="grid grid-cols-1 gap-y-3 pt-2 text-sm">
                            <SpecRow label="Author" value={authors} />
                            <SpecRow label="Illustrated By" value={illustratedBy} />

                            <div className="flex items-start">
                                <span className="w-32 text-gray-500 font-medium">Genre:</span>
                                <div className="flex flex-wrap gap-2">
                                    {genres.length > 0 ? (
                                        genres.map((g) => (
                                            <span
                                                key={g}
                                                className="bg-gray-100 text-gray-700 px-3 py-0.5 rounded-full text-xs font-semibold"
                                            >
                                                {g}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-gray-900 font-medium">-</span>
                                    )}
                                </div>
                            </div>

                            <SpecRow label="Credits" value={credits} />
                            <SpecRow label="Stock Status" value={stockStatus} />
                            <SpecRow label="Copies" value={totalCopies} />

                            <div className="flex items-start">
                                <span className="w-32 text-gray-500 font-medium flex-shrink-0">Description:</span>
                                <div
                                    className="text-gray-700 leading-relaxed text-sm max-w-2xl font-normal"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(description),
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* CLOSE BUTTON AT TOP-RIGHT AREA */}
                    <div className="flex justify-end mt-6">
                        <button
                            onClick={onClose}
                            className="bg-black text-white px-8 py-2.5 rounded-md font-semibold hover:bg-gray-900 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            <hr className="my-8 border-gray-200" />

            {/* LOWER PANEL: TWO TABLES */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-2">
                {/* LEFT TABLE: CURRENT STORE COPIES */}
                <div className="lg:col-span-8">
                    <h4 className="text-xs font-bold text-gray-500 tracking-wider uppercase mb-3">
                        COPIES — CURRENT STORE ({currentStoreName.toUpperCase()})
                    </h4>
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#111111]">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                        Copy ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                        Member
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                        Shelf ID
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-150">
                                {copiesToDisplay.length > 0 ? (
                                    copiesToDisplay.map((c, idx) => (
                                        <tr key={c.copyId || idx} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {c.copyId}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-550">
                                                {getStatusBadge(c.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                                                {c.memberName ? (
                                                    <button
                                                        onClick={() => {
                                                            if (c.memberId) {
                                                                navigate("/members", { state: { memberId: c.memberId } });
                                                            }
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline text-left font-semibold"
                                                    >
                                                        {c.memberName}
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-550 font-medium">
                                                {c.shelfId || "—"}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                                            No copies found for this product.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* RIGHT TABLE: OTHER STORES COPIES */}
                <div className="lg:col-span-4">
                    <h4 className="text-xs font-bold text-gray-500 tracking-wider uppercase mb-3">
                        COPIES — OTHER STORES
                    </h4>
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#111111]">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                        Store
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                        Available copies
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-150">
                                {otherStores.length > 0 ? (
                                    otherStores.map((s, idx) => (
                                        <tr key={s.id || idx} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                {s.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                                                {s.available_copies !== undefined ? s.available_copies : 0}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-500">
                                            No other stores registered.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SpecRow({ label, value }) {
    return (
        <div className="flex items-center">
            <span className="w-32 text-gray-500 font-medium">{label}:</span>
            <span className="text-gray-900 font-semibold">{value}</span>
        </div>
    );
}
